import { BaseApiService, ApiResponse } from './base-api';
import { API_CONFIG } from '../config/api';
import {
  BiometricEnrollmentStart,
  BiometricEnrollmentResponse,
  BiometricStatus,
  BiometricStatusUpdate
} from '../types/auth';

export class Auth0BiometricService extends BaseApiService {
  constructor() {
    super(API_CONFIG.baseURL);
  }

  /**
   * Start biometric enrollment process
   * @param request Biometric enrollment request
   * @returns Enrollment challenge and ID
   */
  async startBiometricEnrollment(
    request: BiometricEnrollmentStart
  ): Promise<ApiResponse<BiometricEnrollmentResponse>> {
    console.log('🔒 Starting biometric enrollment for:', request.email, 'Type:', request.biometricType);
    
    try {
      const response = await this.post<BiometricEnrollmentResponse>(
        '/auth0/biometric/enroll/start',
        request
      );
      
      if (response.success && response.data) {
        console.log('✅ Biometric enrollment started successfully');
        // Store enrollment ID temporarily
        sessionStorage.setItem('biometric_enrollment_id', response.data.enrollmentId);
        sessionStorage.setItem('biometric_type', request.biometricType);
      }
      
      return response;
    } catch (error) {
      console.error('❌ Failed to start biometric enrollment:', error);
      return {
        success: false,
        error: 'BIOMETRIC_ENROLLMENT_START_FAILED',
        message: 'Failed to start biometric enrollment. Please try again.',
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Get current biometric status for the user
   * @returns Current biometric enrollment status
   */
  async getBiometricStatus(): Promise<ApiResponse<BiometricStatus>> {
    console.log('📊 Fetching biometric status...');
    
    try {
      const response = await this.get<BiometricStatus>(
        '/auth0/biometric/status',
        { requireAuth: true }
      );
      
      if (response.success) {
        console.log('✅ Biometric status retrieved successfully');
      }
      
      return response;
    } catch (error) {
      console.error('❌ Failed to fetch biometric status:', error);
      return {
        success: false,
        error: 'BIOMETRIC_STATUS_FAILED',
        message: 'Failed to retrieve biometric status. Please try again.',
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Update biometric status (enable/disable specific biometric type)
   * @param update Biometric status update request
   * @returns Update result
   */
  async updateBiometricStatus(
    update: BiometricStatusUpdate
  ): Promise<ApiResponse<{ success: boolean }>> {
    console.log('🔄 Updating biometric status:', update);
    
    try {
      const response = await this.post<{ success: boolean }>(
        '/auth0/biometric/update-status',
        update,
        { requireAuth: true }
      );
      
      if (response.success) {
        console.log('✅ Biometric status updated successfully');
      }
      
      return response;
    } catch (error) {
      console.error('❌ Failed to update biometric status:', error);
      return {
        success: false,
        error: 'BIOMETRIC_UPDATE_FAILED',
        message: 'Failed to update biometric status. Please try again.',
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Check if biometric authentication is supported by the browser/device
   * @returns Promise resolving to biometric support information
   */
  async checkBiometricSupport(): Promise<{
    supported: boolean;
    availableTypes: string[];
    errors: string[];
  }> {
    console.log('🔍 Checking biometric support...');
    
    const result = {
      supported: false,
      availableTypes: [] as string[],
      errors: [] as string[]
    };

    try {
      // Check for WebAuthn support (required for biometric authentication)
      if (!window.PublicKeyCredential) {
        result.errors.push('WebAuthn not supported');
        return result;
      }

      // Check for platform authenticator (built-in biometric)
      const platformSupported = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      if (platformSupported) {
        result.availableTypes.push('platform');
      }

      // Check for additional biometric APIs if available
      if ('navigator' in window) {
        // Check for Face ID/Touch ID on iOS Safari
        if ('credentials' in navigator && 'webkitCredentials' in navigator) {
          result.availableTypes.push('touchid', 'faceid');
        }

        // Check for Windows Hello
        if ('msCredentials' in navigator) {
          result.availableTypes.push('windows-hello');
        }
      }

      // Check for generic biometric support
      try {
        if ('biometric' in navigator || 'authentication' in navigator) {
          result.availableTypes.push('biometric');
        }
      } catch (error) {
        result.errors.push('Biometric API check failed');
      }

      result.supported = result.availableTypes.length > 0;
      
      console.log('✅ Biometric support check completed:', result);
      return result;
    } catch (error) {
      console.error('❌ Failed to check biometric support:', error);
      result.errors.push(`Support check failed: ${error}`);
      return result;
    }
  }

  /**
   * Perform biometric authentication
   * @param biometricType Type of biometric authentication to use
   * @returns Promise resolving to authentication result
   */
  async authenticateWithBiometric(biometricType: string): Promise<{
    success: boolean;
    credential?: any;
    error?: string;
  }> {
    console.log('🔒 Authenticating with biometric:', biometricType);
    
    try {
      const supportCheck = await this.checkBiometricSupport();
      
      if (!supportCheck.supported) {
        return {
          success: false,
          error: 'Biometric authentication not supported on this device'
        };
      }

      if (!supportCheck.availableTypes.includes(biometricType) && !supportCheck.availableTypes.includes('platform')) {
        return {
          success: false,
          error: `Biometric type '${biometricType}' not available on this device`
        };
      }

      // Use WebAuthn for biometric authentication
      const options: PublicKeyCredentialRequestOptions = {
        challenge: new Uint8Array(32), // This should come from the server
        timeout: 60000,
        userVerification: 'required'
      };

      const credential = await navigator.credentials.get({
        publicKey: options
      });

      if (credential) {
        console.log('✅ Biometric authentication successful');
        return {
          success: true,
          credential: credential
        };
      } else {
        return {
          success: false,
          error: 'Authentication was cancelled or failed'
        };
      }
    } catch (error) {
      console.error('❌ Biometric authentication failed:', error);
      return {
        success: false,
        error: `Authentication failed: ${error}`
      };
    }
  }

  /**
   * Enroll a new biometric credential
   * @param biometricType Type of biometric to enroll
   * @param enrollmentId Enrollment ID from the server
   * @returns Promise resolving to enrollment result
   */
  async enrollBiometricCredential(
    biometricType: string,
    enrollmentId: string
  ): Promise<{
    success: boolean;
    credentialId?: string;
    error?: string;
  }> {
    console.log('🔒 Enrolling biometric credential:', biometricType);
    
    try {
      const supportCheck = await this.checkBiometricSupport();
      
      if (!supportCheck.supported) {
        return {
          success: false,
          error: 'Biometric authentication not supported on this device'
        };
      }

      // Create biometric credential using WebAuthn
      const options: PublicKeyCredentialCreationOptions = {
        challenge: new Uint8Array(32), // This should come from the server
        rp: {
          name: 'CordiantOne',
          id: window.location.hostname
        },
        user: {
          id: new Uint8Array(16), // User ID should come from the server
          name: 'user@example.com', // Should come from context
          displayName: 'User'
        },
        pubKeyCredParams: [
          {
            type: 'public-key',
            alg: -7 // ES256
          }
        ],
        timeout: 60000,
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required'
        }
      };

      const credential = await navigator.credentials.create({
        publicKey: options
      }) as PublicKeyCredential;

      if (credential) {
        console.log('✅ Biometric credential enrolled successfully');
        return {
          success: true,
          credentialId: credential.id
        };
      } else {
        return {
          success: false,
          error: 'Credential creation was cancelled or failed'
        };
      }
    } catch (error) {
      console.error('❌ Biometric enrollment failed:', error);
      return {
        success: false,
        error: `Enrollment failed: ${error}`
      };
    }
  }

  /**
   * Get available biometric types for the current device
   * @returns Promise resolving to available biometric types
   */
  async getAvailableBiometricTypes(): Promise<string[]> {
    const supportCheck = await this.checkBiometricSupport();
    return supportCheck.availableTypes;
  }

  /**
   * Check if a specific biometric type is available
   * @param biometricType Type to check
   * @returns Promise resolving to availability status
   */
  async isBiometricTypeAvailable(biometricType: string): Promise<boolean> {
    const availableTypes = await this.getAvailableBiometricTypes();
    return availableTypes.includes(biometricType) || availableTypes.includes('platform');
  }
}

// Export singleton instance
export const auth0BiometricService = new Auth0BiometricService();